/**
 * The owner of a new unavailability block: coaches or bays.
 */
import { RoleType, UserContextType } from "app/contexts/UserContext";
import {
  BLOCK_FOR_COACHES,
  BLOCK_FOR_RESOURCES,
  BlockOwnerMode as EventBlockOwnerMode,
  EventType,
  isResourceBlock,
} from "app/models/EventModel";

// The model owns the owner mode, because the block payload reads it.
export { BLOCK_FOR_COACHES, BLOCK_FOR_RESOURCES };
export type BlockOwnerMode = EventBlockOwnerMode;

/**
 * Tells if the user adds, edits and deletes the blocks of a bay: an admin, a
 * coach-admin or a scheduler. Front desk staff close bays (2026-10-01). A
 * coach only reads a bay block.
 */
export function canManageResourceBlocks(user: UserContextType): boolean {
  return user.behavesLikeAdmin === true || user.role === RoleType.SCHEDULER;
}

/**
 * Tells if the user blocks bays only. A scheduler cannot block a coach, so
 * the block form of a scheduler is always for bays.
 */
export function blocksResourcesOnly(user: UserContextType): boolean {
  return user.role === RoleType.SCHEDULER;
}

/**
 * Tells if the user can block a bay. The facility must have the feature on,
 * and the user must manage bay blocks. A coach blocks coaches only.
 *
 * The form must also open on the Resource Calendar. Only that view has bay
 * columns, so a bay block made on the Coaches view does not show where the
 * user made it (CFTB-3550).
 */
export function canBlockResources(
  user: UserContextType,
  facility: { featureFlags?: { resourceManagement?: boolean } } | null | undefined,
  bayView: boolean,
): boolean {
  return (
    bayView &&
    facility?.featureFlags?.resourceManagement === true &&
    canManageResourceBlocks(user)
  );
}

type BlockOwnerSource = {
  type?: EventType | string;
  ownerType?: string | null;
  scheduleResourceIds?: number[] | null;
  clickedBayId?: number | null;
};

/**
 * The start values of "Applies to" and of the bay list in the block form.
 *
 * - The edit or the copy of a bay block starts with its bay.
 * - A click on a bay column starts with the bay of the click.
 * - All other forms start with coaches. A lesson copy starts with coaches
 *   too, also when the lesson holds a bay: a copy is not a bay click.
 *
 * A user who cannot block a bay always starts with coaches. A user who
 * blocks bays only always starts with bays, with no bay when the form does
 * not start from a bay.
 */
export function initialBlockOwner(
  event: BlockOwnerSource,
  allowed: boolean,
  resourcesOnly = false,
): { appliesTo: BlockOwnerMode; scheduleResourceIds: number[] } {
  if (!allowed) {
    return { appliesTo: BLOCK_FOR_COACHES, scheduleResourceIds: [] };
  }

  if (isResourceBlock(event)) {
    return {
      appliesTo: BLOCK_FOR_RESOURCES,
      scheduleResourceIds: [...(event.scheduleResourceIds ?? [])],
    };
  }

  if (event.clickedBayId != null) {
    return {
      appliesTo: BLOCK_FOR_RESOURCES,
      scheduleResourceIds: [event.clickedBayId],
    };
  }

  if (resourcesOnly) {
    return { appliesTo: BLOCK_FOR_RESOURCES, scheduleResourceIds: [] };
  }

  return { appliesTo: BLOCK_FOR_COACHES, scheduleResourceIds: [] };
}

/**
 * Tells if the form values make a block for bays. The type must be
 * Unavailability, the user must be able to block a bay, and "Applies to"
 * must be "Resources".
 */
export function blocksResources(
  formValues: { type?: EventType | string; appliesTo?: BlockOwnerMode },
  allowed: boolean,
): boolean {
  return (
    allowed &&
    formValues.type === EventType.COACH_SCHEDULE_BLOCK &&
    formValues.appliesTo === BLOCK_FOR_RESOURCES
  );
}

/**
 * Tells if the block form shows "Applies to" and loads the bays. A new block
 * shows the choice to a user who can block a bay. An edit keeps the owner of
 * the block, so only the edit of a bay block shows the locked choice. The
 * edit of a coach block shows no choice and sends no bay request.
 */
export function showsBlockOwner(
  allowed: boolean,
  locked: boolean,
  appliesTo: BlockOwnerMode | undefined,
): boolean {
  return allowed && (!locked || appliesTo === BLOCK_FOR_RESOURCES);
}

/**
 * The owner fields of a block save from the facility block form. A block for
 * bays keeps the bay list and names no coach. A block for coaches names the
 * coaches and states the Coaches choice, so the payload never sends a bay
 * list that the form only carries from a lesson copy.
 */
export function blockOwnerFields(
  data: {
    type?: EventType | string;
    appliesTo?: BlockOwnerMode;
    scheduleResourceIds?: number[] | null;
    coachProfiles?: { id: string }[];
  },
  allowed: boolean,
): {
  appliesTo: BlockOwnerMode;
  scheduleResourceIds?: number[];
  coachProfileIds?: string[];
} {
  if (blocksResources(data, allowed)) {
    return {
      appliesTo: BLOCK_FOR_RESOURCES,
      scheduleResourceIds: data.scheduleResourceIds ?? [],
      coachProfileIds: undefined,
    };
  }

  return {
    appliesTo: BLOCK_FOR_COACHES,
    scheduleResourceIds: undefined,
    coachProfileIds: (data.coachProfiles ?? []).map((coach) => coach.id),
  };
}
